import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../common/Modal';
import type { CoverNoteData } from '../../types/coverNote';
import { downloadCoverNotePdfByFlow, getCoverNoteByFlow } from '../../services/coverNote';
import { printCoverNotePdf } from '../../utils/coverNotePrint';
import type { WizardFlowKind } from '../../utils/wizardCategoryRoute';
import { CoverNoteBody } from './CoverNoteBody';

type CoverNoteModalProps = {
  open: boolean;
  onClose: () => void;
  /** When set, loads cover note via API (with mock fallback). */
  caseRequestId?: string;
  fileRefFallback?: string;
  /** Static cover note — skips fetch when provided. */
  initialCoverNote?: CoverNoteData | null;
  flow?: WizardFlowKind;
};

export function CoverNoteModal({
  open,
  onClose,
  caseRequestId,
  fileRefFallback,
  initialCoverNote,
  flow = 'criminal',
}: CoverNoteModalProps) {
  const { t } = useTranslation();
  const [coverNote, setCoverNote] = useState<CoverNoteData | null>(initialCoverNote ?? null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setCoverNote(null);
      setLoading(false);
      setError(null);
      return;
    }

    if (initialCoverNote) {
      setCoverNote(initialCoverNote);
      setLoading(false);
      setError(null);
      return;
    }

    const id = caseRequestId?.trim() ?? '';
    const ref = fileRefFallback?.trim() ?? '';
    if (!id && !ref) {
      setLoading(false);
      setError('Case request id or file reference is missing.');
      return;
    }

    let mounted = true;
    setLoading(true);
    setCoverNote(null);
    setError(null);

    void getCoverNoteByFlow(id, flow, ref || undefined).then((r) => {
      if (!mounted) return;
      setLoading(false);
      if (!r.success) {
        setError(r.message);
        return;
      }
      setCoverNote(r.data);
    });

    return () => {
      mounted = false;
    };
  }, [open, caseRequestId, fileRefFallback, initialCoverNote, flow]);

  const fileRef = coverNote?.fileReferenceNumber?.trim() || fileRefFallback?.trim() || '—';
  const requestId = coverNote?.caseRequestId ?? caseRequestId ?? '';

  const downloadPdf = async () => {
    if (downloading || !requestId.trim()) return;
    setDownloading(true);
    try {
      const r = await downloadCoverNotePdfByFlow(requestId, flow);
      if (!r.success) {
        window.alert(r.message ?? 'Cover note PDF could not be loaded.');
        return;
      }
      const safeRef = fileRef.replace(/[^\w.-]+/g, '_');
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Cover-Note-${safeRef}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const printCoverNote = async () => {
    if (printing || !requestId.trim()) return;
    setPrinting(true);
    try {
      const r = await printCoverNotePdf(requestId, flow, fileRefFallback);
      if (!r.success) {
        window.alert(r.message ?? 'Cover note could not be printed.');
      }
    } finally {
      setPrinting(false);
    }
  };

  const displayError = (message: string | null): string => {
    if (!message) return t('caseRequests.coverNoteUnavailable');
    if (message.trim().toLowerCase() === 'qr generation failed') return t('caseRequests.qrGenerationFailed');
    return message;
  };

  return (
    <Modal open={open} title={t('caseRequests.coverNote')} onClose={onClose} boxClassName="cn-mbox-cover-note">
      {loading ? (
        <div style={{ textAlign: 'center', color: 'var(--t3)', padding: '40px 20px' }}>{t('caseRequests.loadingCoverNote')}</div>
      ) : error || !coverNote ? (
        <>
          <div style={{ textAlign: 'center', color: 'var(--t3)', padding: '24px 20px' }}>
            {displayError(error)}
          </div>
          <div className="cn-footer cn-footer--plain">
            <button className="mfb mfb-dk" type="button" onClick={onClose}>
              {t('common.close')}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="cn-printable">
            <CoverNoteBody coverNote={coverNote} />
          </div>
          <div className="cn-footer cn-footer--plain">
            <button className="mfb" type="button" disabled={downloading} onClick={() => void downloadPdf()}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              {downloading ? t('caseRequests.downloading') : t('caseRequests.downloadPdf')}
            </button>
            <button className="mfb" type="button" disabled={printing} onClick={() => void printCoverNote()}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
              {printing ? t('tasks.details.preparing') : t('caseRequests.print')}
            </button>
            <button className="mfb mfb-dk" type="button" onClick={onClose}>
              {t('common.close')}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}
